import { H2 } from "@datapay-by-datacom/design-system";
import { useMemo } from "react";
import { useParams } from "react-router";
import { useAuthenticated } from "../../authn";
import { useUserOrgs } from "../../org/hooks";
import { useShadowRunProgress } from "../hooks/useShadowRunProgress";
import { StandardProgress } from "../components/StandardProgress/StandardProgress";
import { EnterpriseProgress } from "../components/EnterpriseProgress/EnterpriseProgress";
import styles from "./ProgressMonitorScreen.module.css";

export function ProgressMonitorScreen() {
  const { agentId, workflowId } = useParams<{
    agentId: string;
    workflowId: string;
  }>();

  if (!agentId || !workflowId) {
    return (
      <div className={styles.page} data-testid="progress-monitor-error">
        <H2>Shadow Run Progress</H2>
        <p className={styles.message}>No workflow ID provided.</p>
      </div>
    );
  }

  return <ProgressMonitorContent agentId={agentId} workflowId={workflowId} />;
}

function ProgressMonitorContent({
  agentId,
  workflowId,
}: {
  agentId: string;
  workflowId: string;
}) {
  const { data, isLoading, isError } = useShadowRunProgress(agentId, workflowId);
  const auth = useAuthenticated();
  const userId = auth.state === "authenticated" ? auth.userId : "";
  const userOrgsQuery = useUserOrgs(userId);

  const orgName = useMemo(() => {
    if (!data?.org_id || !userOrgsQuery.data) return undefined;
    const match = userOrgsQuery.data.find(
      (o) => o.id === data.org_id && o.category === data.org_category,
    );
    return match?.name;
  }, [data?.org_id, data?.org_category, userOrgsQuery.data]);

  if (isLoading) {
    return (
      <div className={styles.page} data-testid="progress-monitor-loading">
        <H2>Shadow Run Progress</H2>
        <p className={styles.message}>Loading progress...</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.page} data-testid="progress-monitor-error">
        <H2>Shadow Run Progress</H2>
        <p className={styles.message}>
          Failed to load progress for workflow {workflowId}.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="progress-monitor">
      <H2>Shadow Run Progress</H2>
      {data.shard_summaries === null ? (
        <StandardProgress progress={data} orgName={orgName} />
      ) : (
        <EnterpriseProgress progress={data} orgName={orgName} />
      )}
    </div>
  );
}
